
import React, { useState, useRef, useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, ArrowDown, ThumbsUp, ThumbsDown } from 'lucide-react';
import ChatMessage from '@/components/chat/ChatMessage';
import IntroBanner from '@/components/chat/IntroBanner';
import ChatSidebar from '@/components/chat/ChatSidebar';
import { Message } from '@/types/chat';

const AIChat = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const suggestions = [
    'Como a dgenny pode reduzir minhas compras B2B?',
    'Como funciona a negociação em escala pelo WhatsApp?',
    'Quais setores mais se beneficiam da dgenny?',
    'Quais planos existem para construtoras?',
  ];

  // Simulate sending a message - will be replaced with actual API calls
  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!input.trim()) return;
    
    // Add user message
    const userMessage = { 
      id: Date.now().toString(), 
      content: input.trim(), 
      role: 'user' as const,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Simulate API delay
    setTimeout(() => {
      // This is where you'll connect to various APIs (OpenAI, Anthropic, etc.)
      // For now, we'll just add mock responses
      const botResponses: Record<string, string> = {
        default: 'A dgenny é a I.A. que negocia compras B2B em paralelo — no WhatsApp, com dezenas de fornecedores ao mesmo tempo. Clientes economizam até 80% nas compras. Quer ver em um caso real?',
      };

      // Add bot response
      const botMessage = { 
        id: (Date.now() + 1).toString(), 
        content: botResponses.default, 
        role: 'assistant' as const,
        timestamp: new Date() 
      };
      
      setMessages(prev => [...prev, botMessage]);
      setIsLoading(false);
    }, 1500);
  };

  // Handle clicking on a suggested message
  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    handleSendMessage();
  };

  return (
    <div className="flex flex-col min-h-screen bg-muted/30">
      <Navbar />

      <div className="flex flex-1 overflow-hidden">
        <div className={`${showSidebar ? 'block' : 'hidden'} md:block w-72 lg:w-80 border-r border-border bg-white`}>
          <ChatSidebar onCloseSidebar={() => setShowSidebar(false)} />
        </div>
        
        {/* Main chat area */}
        <div className="flex-1 flex flex-col max-h-[calc(100vh-64px)]">
          {/* Chat messages */}
          <div className="flex-1 overflow-y-auto p-4">
            {messages.length === 0 ? (
              <IntroBanner 
                onSuggestionClick={handleSuggestionClick}
                suggestions={suggestions}
              />
            ) : (
              <>
                {messages.map(message => (
                  <ChatMessage 
                    key={message.id} 
                    message={message} 
                  />
                ))}
                {isLoading && (
                  <div className="flex items-center space-x-2 p-4 max-w-3xl mx-auto">
                    <div className="w-8 h-8 rounded-full bg-brand-roxo/10 flex items-center justify-center">
                      <div className="w-2 h-2 bg-brand-roxo rounded-full animate-pulse"></div>
                    </div>
                    <p className="text-brand-azul/60">dgenny está digitando…</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
          
          <div className="border-t border-border bg-white p-4">
            <form onSubmit={handleSendMessage} className="max-w-3xl mx-auto">
              <div className="relative">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Fale com a dgenny…"
                  className="pr-12 min-h-[60px] max-h-40 resize-none rounded-2xl"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!input.trim() || isLoading}
                  className="absolute right-2 bottom-2 bg-brand-roxo hover:bg-brand-roxo/90 rounded-xl"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-brand-azul/50 mt-2 text-center">
                dgenny é uma I.A. de compras B2B. Suas interações podem ser usadas para melhorar o serviço.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChat;
